import { DocumentFileType as Document_File_Type_Enum } from '@risksmart-app/domain/src/types/consts/document-file-type';
import {
  ParentTypes as Parent_Type_Enum,
  ParentTypes,
} from '@risksmart-app/domain/src/types/consts/parent-type';
import { VersionStatus as Version_Status_Enum } from '@risksmart-app/domain/src/types/consts/version-status';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import ConfirmModal from 'src/components/confirm-modal/ConfirmModal';
import { PageForm } from 'src/components/form/form/PageForm';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';
import type { Editor as TinyEditor } from 'tinymce';

import {
  useInsertDocumentVersion,
  useUpdateDocumentVersion,
} from '@/hooks/mutations/document-file';
import { useFileUpload } from '@/hooks/mutations/file/useFileUpload';
import { useGetDocumentById, useGetDocumentFileById } from '@/hooks/queries';
import { useChangeRequests } from '@/hooks/useChangeRequests';
import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';

import type { DocumentVersionFormFieldData } from '../../../forms/documentFileSchema';
import {
  defaultValues,
  DocumentFileFormSchema,
} from '../../../forms/documentFileSchema';
import DocumentVersionForm from '../../../forms/DocumentVersionForm';
import DocumentVersionFormFields from '../../../forms/DocumentVersionFormFields';
import { useNextFileVersion } from '../../../useNextFileVersion';

type TabProps = {
  parentDocumentId: string;
  documentFileId: string;
};

const Tab = ({ parentDocumentId, documentFileId }: TabProps) => {
  const approversEnabled = useIsModuleEnabled('approval');
  const { t } = useTranslation(['common'], {
    keyPrefix: 'documentFiles.confirm_close_modal',
  });
  const { t: st } = useTranslation(['common']);
  const editorRef = useRef<null | TinyEditor>(null);
  const uploadFile = useFileUpload();
  const [confirmCloseVisible, setConfirmCloseVisible] = useState(false);
  const { insertDocumentVersion } = useInsertDocumentVersion();
  const { updateDocumentVersion } = useUpdateDocumentVersion();
  const { data: parentData } = useGetDocumentById({
    queryArgs: { documentId: parentDocumentId },
  });
  const parent = parentData?.document[0];

  const [
    { nextVersion, content, type, link, customAttributeData },
    { loading: loadingNextFileVersion },
  ] = useNextFileVersion(parentDocumentId);
  const navigate = useNavigate();
  const {
    hasPermission: canEditPermission,
    loading: canEditPermissionLoading,
  } = useHasPermissionQuery('update:document_file', parent);
  const {
    hasPermission: canCreatePermission,
    loading: canCreatePermissionLoading,
  } = useHasPermissionQuery('insert:document_file', parent);

  const {
    data: documentFileData,
    loading,
    error,
  } = useGetDocumentFileById({
    queryArgs: { id: documentFileId },
    shouldSkip: !documentFileId,
  });
  if (error) {
    throw error;
  }

  const documentFile = documentFileData?.document_file[0];

  const canModify = documentFile
    ? canEditPermission && !canEditPermissionLoading
    : canCreatePermission && !canCreatePermissionLoading;
  const isDocumentFileDraft = documentFile?.Status
    ? documentFile?.Status === Version_Status_Enum.Draft
    : true;
  const onSave = async (data: DocumentVersionFormFieldData) => {
    const sharedFields = {
      Version: data.Version,
      Summary: data.Summary,
      ReasonForReview: data.ReasonForReview,
      ReviewedBy: data.ReviewedBy?.value,
      ReviewDate: data.ReviewDate,
      NextReviewDate: data.NextReviewDate,
      CustomAttributeData: data.CustomAttributeData,
    };

    if (documentFile) {
      const updateSharedFields = {
        ...sharedFields,
        Id: documentFile.Id,
        LatestModifiedAtTimestamp: documentFile.ModifiedAtTimestamp,
        Status: data.Status,
      };
      switch (data.Type) {
        case Document_File_Type_Enum.Link:
          await updateDocumentVersion(
            {
              ...updateSharedFields,
              Type: Document_File_Type_Enum.Link,
              Link: data.Link,
            },
            { confirmChangeRequest: true }
          );
          break;
        case Document_File_Type_Enum.Html: {
          const content = editorRef.current?.getContent() ?? '';
          await updateDocumentVersion(
            {
              ...updateSharedFields,
              Type: Document_File_Type_Enum.Html,
              Content: content,
            },
            { confirmChangeRequest: true }
          );
          break;
        }
        case Document_File_Type_Enum.File: {
          const files = data.files || [];
          const newFile = files.find((f) => f instanceof File);
          let fileId = '';
          if (newFile) {
            const uploadResponse = await uploadFile({
              parentType: ParentTypes.Document,
              parentIds: [parentDocumentId],
              newFiles: [newFile],
            });
            fileId = uploadResponse.fileIds[0];
          } else {
            fileId = documentFile?.file?.Id ?? '';
          }
          await updateDocumentVersion(
            {
              ...updateSharedFields,
              Type: Document_File_Type_Enum.File,
              FileId: fileId,
            },
            { confirmChangeRequest: true }
          );
          break;
        }
      }
    } else {
      const insertSharedFields = {
        ...sharedFields,
        ParentDocumentId: parentDocumentId,
      };
      switch (data.Type) {
        case Document_File_Type_Enum.Link:
          await insertDocumentVersion({
            ...insertSharedFields,
            Type: Document_File_Type_Enum.Link,
            Link: data.Link,
          });
          break;
        case Document_File_Type_Enum.Html: {
          const content = editorRef.current?.getContent() ?? '';
          await insertDocumentVersion({
            ...insertSharedFields,
            Type: Document_File_Type_Enum.Html,
            Content: content,
          });
          break;
        }
        case Document_File_Type_Enum.File: {
          const files = data.files || [];
          const newFile = files.find((f) => f instanceof File);
          let fileId = '';
          if (newFile) {
            const uploadResponse = await uploadFile({
              parentType: ParentTypes.Document,
              parentIds: [parentDocumentId],
              newFiles: [newFile],
            });
            fileId = uploadResponse.fileIds[0];
          }
          await insertDocumentVersion({
            ...insertSharedFields,
            Type: Document_File_Type_Enum.File,
            FileId: fileId,
          });
          break;
        }
      }
    }
  };

  const approvalData = documentFile
    ? {
        ...documentFile,
        owners: documentFile.parent?.owners ?? [],
        ownerGroups: documentFile.parent?.ownerGroups ?? [],
      }
    : undefined;

  const { pendingChangeRequests } = useChangeRequests(approvalData);

  // Build the form values per `Type` discriminant so the resulting object
  // satisfies the discriminated-union form schema without a cast. Each
  // branch supplies only the field that variant requires
  // (`Link` / `Content` / `files`).
  const buildValues = (): DocumentVersionFormFieldData | undefined => {
    if (!documentFile) {
      return undefined;
    }
    const shared = {
      Version: documentFile.Version,
      Summary: documentFile.Summary,
      Status: documentFile.Status,
      ReasonForReview: documentFile.ReasonForReview,
      ReviewedBy: documentFile.ReviewedBy
        ? { value: documentFile.ReviewedBy, type: 'user' as const }
        : null,
      ReviewDate: documentFile.ReviewDate,
      NextReviewDate: documentFile.NextReviewDate,
      CustomAttributeData: documentFile.CustomAttributeData,
    };
    switch (documentFile.Type) {
      case Document_File_Type_Enum.Link:
        return {
          ...shared,
          Type: Document_File_Type_Enum.Link,
          Link: documentFile.Link ?? '',
        };
      case Document_File_Type_Enum.Html:
        return {
          ...shared,
          Type: Document_File_Type_Enum.Html,
          Content: documentFile.Content ?? '',
        };
      case Document_File_Type_Enum.File:
        return {
          ...shared,
          Type: Document_File_Type_Enum.File,
          files: documentFile.file ? [documentFile.file] : [],
        };
    }
  };
  const values = buildValues();

  // Default values for the "create new version" path. Branches on the
  // pre-resolved `type` so the resulting object satisfies the
  // discriminated-union form schema without a cast.
  const buildDefaultValuesWithVersion = (): DocumentVersionFormFieldData => {
    const shared = {
      ...defaultValues,
      Version: nextVersion,
      CustomAttributeData: customAttributeData,
    };
    switch (type) {
      case Document_File_Type_Enum.Link:
        return {
          ...shared,
          Type: Document_File_Type_Enum.Link,
          Link: link ?? '',
        };
      case Document_File_Type_Enum.Html:
        return {
          ...shared,
          Type: Document_File_Type_Enum.Html,
          Content: content ?? '',
        };
      case Document_File_Type_Enum.File:
        return { ...shared, Type: Document_File_Type_Enum.File, files: [] };
    }
  };
  const defaultValuesWithVersion = buildDefaultValuesWithVersion();
  const readOnly = !canModify;

  if (loading || loadingNextFileVersion) {
    return null;
  }

  const onDismiss = () => {
    navigate(`/policy/${parentDocumentId}/files`);
  };

  return approversEnabled && documentFile?.Id ? (
    <>
      <DocumentVersionForm
        // Disable status field as we have buttons which allow correct status transitions
        disableStatus={true}
        defaultValues={defaultValuesWithVersion}
        values={values}
        onSave={onSave}
        onDismiss={(saved) => {
          if (saved || readOnly || !isDocumentFileDraft) {
            onDismiss();
          }
          setConfirmCloseVisible(true);
        }}
        readOnly={readOnly}
        approvalConfig={{
          object: approvalData,
        }}
        hasPendingChangeRequests={pendingChangeRequests.length >= 1}
        isCreatingNewEntity={!!documentFile}
        editorRef={editorRef}
        readonly={readOnly}
        savedStatus={documentFile?.Status}
        parentId={parentDocumentId}
      />

      {confirmCloseVisible && (
        <ConfirmModal
          isVisible={true}
          onConfirm={onDismiss}
          onDismiss={() => setConfirmCloseVisible(false)}
          header={t('title')}
        >
          {t('message')}
        </ConfirmModal>
      )}
    </>
  ) : (
    <>
      <PageForm
        // TODO: Add render template?
        header={st('details')}
        i18n={st('documentFiles')}
        defaultValues={defaultValuesWithVersion}
        parentType={Parent_Type_Enum.DocumentFile}
        values={values}
        schema={DocumentFileFormSchema}
        onSave={onSave}
        onDismiss={(saved) => {
          if (saved || readOnly) {
            onDismiss();
          }
          setConfirmCloseVisible(true);
        }}
        formId={'document-file-form'}
        readOnly={readOnly}
      >
        <DocumentVersionFormFields
          disableStatus={false}
          isCreatingNewEntity={!!documentFile}
          editorRef={editorRef}
          readonly={readOnly}
          savedStatus={documentFile?.Status || Version_Status_Enum.Draft}
          parentId={parentDocumentId}
        />
      </PageForm>
      <ConfirmModal
        isVisible={confirmCloseVisible}
        onConfirm={onDismiss}
        onDismiss={() => setConfirmCloseVisible(false)}
        header={t('title')}
      >
        {t('message')}
      </ConfirmModal>
    </>
  );
};

export default Tab;
